import type { IconType } from 'react-icons';

import { MessagingPlatform, PersonOrigin } from '@this+that/models';
import { LuGlobe, LuMail, LuMessageSquare } from 'react-icons/lu';

import type { MessageIconKind } from './utils/formatMessage';
import gmailIcon from '../../../assets/icons/gmail_icon.svg';
import outlookIcon from '../../../assets/icons/outlook_icon.svg';
import slackIcon from '../../../assets/icons/slack_icon.svg';
import thisThatIcon from '../../../assets/images/this+that_green_icon.svg';
import { PersonActivityPlatform } from '../../../services/crm.service';
import type { OriginFilter } from '../../../store/Contacts';

export interface OriginFilterOption {
  value: OriginFilter;
  labelKey: string;
}

export const DEFAULT_ORIGIN_FILTER: OriginFilter = 'all';

export const ORIGIN_FILTERS: OriginFilterOption[] = [
  { value: DEFAULT_ORIGIN_FILTER, labelKey: 'contacts.filters.all' },
  { value: 'google', labelKey: 'contacts.filters.google' },
  { value: 'outlook', labelKey: 'contacts.filters.outlook' },
  { value: 'slack', labelKey: 'contacts.filters.slack' },
  { value: 'landingPage', labelKey: 'contacts.filters.landingPage' },
];

export type ContactSourceKind =
  | 'google'
  | 'outlook'
  | 'slack'
  | 'thisThat'
  | 'landingPage'
  | 'other';

export const SOURCE_KIND_ICON_ORDER: ContactSourceKind[] = [
  'google',
  'outlook',
  'slack',
  'thisThat',
  'landingPage',
  'other',
];

export const SOURCE_KIND_ORIGIN_MAP: Record<
  Exclude<ContactSourceKind, 'other'>,
  string[]
> = {
  google: [MessagingPlatform.GOOGLE, MessagingPlatform.GMAIL],
  outlook: [MessagingPlatform.OUTLOOK, MessagingPlatform.MICROSOFT],
  slack: [MessagingPlatform.SLACK, MessagingPlatform.SLACK_BOT],
  thisThat: [MessagingPlatform.THIS_THAT, PersonOrigin.TEAM],
  landingPage: [PersonOrigin.LANDING_PAGE],
};

export const ORIGIN_FILTER_MAP: Record<
  Exclude<OriginFilter, 'all'>,
  string[]
> = {
  google: SOURCE_KIND_ORIGIN_MAP.google,
  outlook: SOURCE_KIND_ORIGIN_MAP.outlook,
  slack: SOURCE_KIND_ORIGIN_MAP.slack,
  landingPage: SOURCE_KIND_ORIGIN_MAP.landingPage,
};

export enum MessagesFilter {
  ALL = 'all',
  EMAIL = 'email',
  SLACK = 'slack',
}

export const MESSAGES_FILTER_PLATFORMS: Record<
  MessagesFilter,
  PersonActivityPlatform[] | undefined
> = {
  [MessagesFilter.ALL]: undefined,
  [MessagesFilter.EMAIL]: [
    PersonActivityPlatform.GMAIL,
    PersonActivityPlatform.OUTLOOK,
  ],
  [MessagesFilter.SLACK]: [PersonActivityPlatform.SLACK],
};

export interface MessagesFilterOption {
  value: MessagesFilter;
  labelKey: string;
}

export const MESSAGES_FILTER_OPTIONS: MessagesFilterOption[] = [
  { value: MessagesFilter.ALL, labelKey: 'contacts.messages.filters.all' },
  { value: MessagesFilter.EMAIL, labelKey: 'contacts.messages.filters.email' },
  { value: MessagesFilter.SLACK, labelKey: 'contacts.messages.filters.slack' },
];

export const MESSAGES_FILTER_EMPTY_KEY: Record<MessagesFilter, string> = {
  [MessagesFilter.ALL]: 'contacts.messages.empty',
  [MessagesFilter.EMAIL]: 'contacts.messages.emptyEmail',
  [MessagesFilter.SLACK]: 'contacts.messages.emptySlack',
};

export const AUTHOR_AVATAR_COLOR_VAR = 'var(--color-emerald)';

// Avatars are decorative — exempt from the semantic-token-only styling rule.
export const AVATAR_PALETTE: string[] = [
  '#0b5a44',
  '#4f46e5',
  '#0891b2',
  '#b45309',
  '#9d174d',
  '#4d7c0f',
  '#6d28d9',
  '#0f6cbd',
  '#be185d',
];

export const NOTES_DEBOUNCE_MS = 600;
export const NOTES_SAVED_RESET_MS = 2000;
export const CONTACTS_PAGE_SIZE = 50;
export const CONTACT_EVENTS_PAGE_SIZE = 20;
export const CONTACT_MESSAGES_PAGE_SIZE = 20;
// The list isn't virtualized — every loaded message stays a DOM node — so this caps growth (25 pages of CONTACT_MESSAGES_PAGE_SIZE).
export const MAX_LOADED_MESSAGES = 500;
export const SEARCH_DEBOUNCE_MS = 300;
export const SEARCH_MIN_QUERY_LENGTH = 2;
export const MERGE_TIMELINE_NOTICE_TIMEOUT_MS = 30_000;
// Mirrors the backend cap: apps/api/src/features/crm/utils/constants.ts MAX_PERSON_IDS_FOR_MERGE
export const MAX_MERGE_SELECTION = 50;
export const EVENT_TIME_FORMAT = 'HH:mm';
// Coalesces a burst of WebSocket INSERT events for the same contact into one refetch.
// Mirrors CALENDAR_EVENTS_SYNC_DEBOUNCE_MS in context/WebSocketSync/syncs/calendarEventsSync.ts.
export const CONTACT_ACTIVITY_REALTIME_DEBOUNCE_MS = 1000;
// Same duration/affordance as the Inbox's HIGHLIGHT_DURATION_MS
// (context/WebSocketSync/syncs/userCommunicationsSync.ts), reused not reinvented.
export const MESSAGE_HIGHLIGHT_DURATION_MS = 3000;
// Caps getPerson calls useTeamContactRealtime fires per burst window — an automation
// bulk-creating N team contacts would otherwise fire N getPerson calls per member.
export const TEAM_CONTACT_REALTIME_BURST_LIMIT = 5;
// Window the burst limit above resets on. Mirrors CONTACT_ACTIVITY_REALTIME_DEBOUNCE_MS's
// order of magnitude — long enough to catch one automation batch, short enough that a
// legitimate slower trickle of new team contacts isn't held back after it.
export const TEAM_CONTACT_REALTIME_BURST_WINDOW_MS = 10_000;

export const SOURCE_KIND_PLATFORM_ICON_SRC: Record<
  ContactSourceKind,
  string | null
> = {
  google: gmailIcon,
  outlook: outlookIcon,
  slack: slackIcon,
  thisThat: thisThatIcon,
  landingPage: null,
  other: null,
};

// Kinds with no platform logo asset render a react-icon instead — see
// resolveOriginIconForKind in decorateContact.ts, which prefers the image
// src map above and falls back to this one.
export const SOURCE_KIND_ICON_COMPONENT: Partial<
  Record<ContactSourceKind, IconType>
> = {
  landingPage: LuGlobe,
};

export interface MessageIconConfig {
  icon: IconType;
  colorClassName: string;
}

export const MESSAGE_ICON_CONFIG: Record<MessageIconKind, MessageIconConfig> = {
  'email-in': { icon: LuMail, colorClassName: 'text-semantic-text-secondary' },
  'email-out': { icon: LuMail, colorClassName: 'text-semantic-emerald' },
  'message-in': {
    icon: LuMessageSquare,
    colorClassName: 'text-semantic-text-secondary',
  },
  'message-out': {
    icon: LuMessageSquare,
    colorClassName: 'text-semantic-emerald',
  },
};

export const CONTACTS_LIST_COLUMN_WIDTH = 320;
export const CONTACTS_DETAIL_MIN_WIDTH = 440;
export const CONTACTS_TWO_PANE_MIN_WIDTH =
  CONTACTS_LIST_COLUMN_WIDTH + CONTACTS_DETAIL_MIN_WIDTH;
// Detail width below which Notes/Timeline stack above Messages. Mirrors `@[880px]/detail:` in ContactDetail.
export const CONTACT_DETAIL_STACK_MIN_WIDTH = 880;
// Detail width below which toolbar and actions drop their labels: Edit + New contact (~260px) next to the identity block stops fitting. Mirrors `@max-[640px]/detail:` below.
export const CONTACT_DETAIL_ICON_ONLY_MAX_WIDTH = 640;

// Absent on mobile (no `detail` container there), so labels stay visible.
export const CONTACT_ICON_ONLY_CLASS_NAMES = {
  label: '@max-[640px]/detail:hidden',
  padding: '@max-[640px]/detail:px-2.5',
};

export enum ContactsPaneMode {
  TWO_PANE = 'two-pane',
  ONE_PANE = 'one-pane',
}

export const CONTACTS_TEST_IDS = {
  layout: 'contacts-layout',
  listPane: 'contacts-list-pane',
  detailPane: 'contacts-detail-pane',
  detailBody: 'contact-detail-body',
  detailHeader: 'contact-detail-header',
  editButton: 'contact-detail-edit-button',
  newButton: 'contact-detail-new-button',
  headerCreateButton: 'contacts-header-create-button',
  backButton: 'contact-detail-back-button',
  listRow: 'contact-list-row',
};
